import { useEffect, useRef, useState } from "react";
import { Search, X, AtSign, FileText, DoorOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { SECTION_MAP } from "@/lib/sections";

const SECTION_LABELS = Object.fromEntries(
  Object.entries(SECTION_MAP).map(([slug, s]) => [slug, s.label])
);

export const SearchDrawer = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ people: [], posts: [], rooms: [] });
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 80);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setQ("");
      setResults({ people: [], posts: [], rooms: [] });
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setResults({ people: [], posts: [], rooms: [] });
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim(), limit: 8 } });
        if (!cancelled) setResults(data);
      } catch {
        if (!cancelled) setResults({ people: [], posts: [], rooms: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, open]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const total = results.people.length + results.posts.length + results.rooms.length;

  return (
    <>
      <button
        type="button"
        data-testid="search-trigger"
        onClick={() => setOpen(true)}
        aria-label="Search the salon"
        title="Search the salon"
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 4,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Search size={20} strokeWidth={1.4} color="var(--text-primary)" />
      </button>

      {open && (
        <div
          data-testid="search-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(51,51,51,0.35)",
            backdropFilter: "blur(8px)",
            zIndex: 300,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            padding: "10vh 16px 16px",
          }}
        >
          <div
            data-testid="search-drawer"
            style={{
              width: "100%",
              maxWidth: 640,
              maxHeight: "80vh",
              background: "var(--surface)",
              border: "1px solid var(--border-default)",
              boxShadow: "0 24px 64px rgba(51,51,51,0.18)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Input bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "20px 24px",
                borderBottom: "1px solid var(--border-default)",
              }}
            >
              <Search size={18} strokeWidth={1.4} color="var(--text-muted)" />
              <input
                ref={inputRef}
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search the salon…"
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 22,
                  fontStyle: "italic",
                  color: "var(--text-primary)",
                }}
              />
              <button
                type="button"
                data-testid="search-close"
                onClick={() => setOpen(false)}
                aria-label="Close search"
                style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4 }}
              >
                <X size={18} strokeWidth={1.4} color="var(--text-muted)" />
              </button>
            </div>

            {/* Results */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {!q.trim() ? (
                <div style={{ padding: "40px 24px", textAlign: "center" }}>
                  <p
                    className="editorial-body"
                    style={{ fontStyle: "italic", color: "var(--text-muted)", fontSize: 15, lineHeight: 1.6 }}
                  >
                    Look for a name, a handle, a room, or a few words from a post.
                  </p>
                  <p
                    className="overline"
                    style={{ marginTop: 16, fontSize: 10, color: "var(--text-muted)", opacity: 0.7 }}
                  >
                    Try: <em>cuisine</em> · <em>céleste</em> · <em>croissant</em>
                  </p>
                </div>
              ) : loading && total === 0 ? (
                <div style={{ padding: "40px 24px", textAlign: "center" }}>
                  <p className="editorial-body" style={{ fontStyle: "italic", color: "var(--text-muted)", fontSize: 14 }}>
                    Looking…
                  </p>
                </div>
              ) : total === 0 ? (
                <div style={{ padding: "40px 24px", textAlign: "center" }}>
                  <p className="editorial-body" style={{ fontStyle: "italic", color: "var(--text-muted)", fontSize: 15, lineHeight: 1.6 }}>
                    Nothing here by that name. The salon may simply be quiet on this.
                  </p>
                </div>
              ) : (
                <>
                  {results.rooms.length > 0 && (
                    <Section title="Rooms">
                      {results.rooms.map((r) => (
                        <button
                          key={r.slug}
                          type="button"
                          data-testid={`search-room-${r.slug}`}
                          onClick={() => go(`/room/${r.slug}`)}
                          style={rowStyle}
                        >
                          <DoorOpen size={18} strokeWidth={1.4} color="var(--brand-accent)" />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={titleStyle}>{r.label}</div>
                            <div style={metaStyle}>Room · /{r.slug}</div>
                          </div>
                        </button>
                      ))}
                    </Section>
                  )}
                  {results.people.length > 0 && (
                    <Section title="People">
                      {results.people.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          data-testid={`search-person-${u.username}`}
                          onClick={() => go(`/profile/${u.username}`)}
                          style={rowStyle}
                        >
                          {u.avatar_url ? (
                            <img
                              loading="lazy" decoding="async" src={u.avatar_url}
                              alt=""
                              style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "1px solid var(--border-default)" }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                background: "var(--bg-alt)",
                                border: "1px solid var(--border-default)",
                                display: "grid",
                                placeItems: "center",
                              }}
                            >
                              <AtSign size={14} strokeWidth={1.4} />
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={titleStyle}>{u.display_name}</div>
                            <div style={metaStyle}>
                              @{u.username}{u.location ? ` · ${u.location}` : ""}
                            </div>
                          </div>
                        </button>
                      ))}
                    </Section>
                  )}
                  {results.posts.length > 0 && (
                    <Section title="Posts">
                      {results.posts.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          data-testid={`search-post-${p.id}`}
                          onClick={() => go(`/post/${p.id}`)}
                          style={rowStyle}
                        >
                          {p.media_url ? (
                            <img
                              loading="lazy" decoding="async" src={p.media_url}
                              alt=""
                              style={{ width: 44, height: 44, objectFit: "cover", border: "1px solid var(--border-default)" }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 44,
                                height: 44,
                                background: "var(--bg-alt)",
                                border: "1px solid var(--border-default)",
                                display: "grid",
                                placeItems: "center",
                              }}
                            >
                              <FileText size={16} strokeWidth={1.4} color="var(--text-muted)" />
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ ...titleStyle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {p.title || p.caption || "Untitled"}
                            </div>
                            <div style={metaStyle}>
                              {p.section ? `${SECTION_LABELS[p.section] || p.section} · ` : ""}
                              {p.author?.display_name || ""}
                            </div>
                          </div>
                        </button>
                      ))}
                    </Section>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div
      className="overline"
      style={{
        padding: "14px 24px 8px",
        color: "var(--brand-accent)",
        fontSize: 10,
      }}
    >
      {title}
    </div>
    <div>{children}</div>
  </div>
);

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  width: "100%",
  padding: "12px 24px",
  background: "transparent",
  border: "none",
  borderBottom: "1px solid var(--border-default)",
  textAlign: "left",
  cursor: "pointer",
  transition: "background 0.15s ease",
};

const titleStyle = {
  fontFamily: "'Cormorant Garamond', serif",
  fontSize: 17,
  color: "var(--text-primary)",
  lineHeight: 1.2,
};

const metaStyle = {
  fontFamily: "'Outfit', sans-serif",
  fontSize: 11,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
  marginTop: 3,
};

export default SearchDrawer;
